import { MATERIAL_PROFILE_TYPES } from '../Constants/Constants'
import {
    inchesToMm,
    mmToInches,
    numbersWithinPercentageTolerance,
    organisationDimensionTolerance,
    organisationLengthPrecision,
} from '../GeneralUtils/GeneralUtils'

export const sheetVolume = (thickness, width, height, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        width = width / 12
        height = height / 12
    }

    result = height * width * thickness

    return result
}

export const rectangleVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result = materialLength * (width * height - (width - 2 * thickness) * (height - 2 * thickness))

    return result
}

export const circleVolume = (thickness, materialLength, diameter, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        diameter = diameter / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        diameter = diameter / 12
    }

    result = materialLength * Math.PI * (Math.pow(diameter / 2, 2) - Math.pow((diameter - 2 * thickness) / 2, 2))

    return result
}

const ovalVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0
    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result =
        materialLength *
        ((width - height) * 2 * thickness +
            Math.PI * (Math.pow(height / 2, 2) - Math.pow((height - 2 * thickness) / 2, 2)))

    return result
}

const angleVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result = materialLength * ((width - thickness) * thickness + height * thickness)

    return result
}

const uChannelVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result = materialLength * (width * thickness + (height - thickness) * thickness * 2)

    return result
}

const tBeamVolume = (thickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result = materialLength * (width * thickness + (height - thickness) * thickness)

    return result
}

const iBeamVolume = (thickness, webThickness, materialLength, width, height, useImperialUnits) => {
    let result = 0.0

    // A missing webThickness contributes no web area rather than poisoning the whole result
    // with NaN — an I-beam record whose web dimension is not filled in still yields the flange
    // weight. Form validation, not this calculation, is what blocks an incomplete I-beam.
    webThickness = webThickness || 0

    if (!useImperialUnits) {
        thickness = thickness / 1000
        webThickness = webThickness / 1000
        materialLength = materialLength / 1000
        width = width / 1000
        height = height / 1000
    } else {
        thickness = thickness / 12
        webThickness = webThickness / 12
        materialLength = materialLength / 12
        width = width / 12
        height = height / 12
    }

    result = materialLength * (width * thickness * 2 + (height - thickness * 2) * webThickness)

    return result
}

export const sheetWeight = ({ density, height, thickness, useImperialUnits, width }) => {
    return density * sheetVolume(thickness, width, height, useImperialUnits)
}

export const rectangleWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * rectangleVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const circleWeight = ({ density, diameter, materialLength, thickness, useImperialUnits }) => {
    return density * circleVolume(thickness, materialLength, diameter, useImperialUnits)
}

export const ovalWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * ovalVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const angleWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * angleVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const uChannelWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * uChannelVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const tBeamWeight = ({ density, height, materialLength, thickness, useImperialUnits, width }) => {
    return density * tBeamVolume(thickness, materialLength, width, height, useImperialUnits)
}

export const iBeamWeight = ({ density, height, materialLength, thickness, useImperialUnits, webThickness, width }) => {
    return density * iBeamVolume(thickness, webThickness, materialLength, width, height, useImperialUnits)
}

const materialWeight = (
    profile,
    density,
    thickness,
    materialLength,
    width,
    height,
    useImperialUnits,
    diameter,
    webThickness
) => {
    let mWeight = 0.0
    switch (profile) {
        case 'Rectangle':
        case 'Square':
            mWeight = rectangleWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
            break
        case 'Circle':
            mWeight = circleWeight({
                density,
                thickness,
                materialLength,
                diameter,
                useImperialUnits,
            })
            break
        case 'FlatSidedOval':
            mWeight = ovalWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
            break
        case 'Angle':
            mWeight = angleWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
            break
        case 'UChannel':
            mWeight = uChannelWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
            break
        case 'TBeam':
            mWeight = tBeamWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
            })
            break
        case 'IBeam':
            mWeight = iBeamWeight({
                density,
                thickness,
                materialLength,
                width,
                height,
                useImperialUnits,
                webThickness,
            })
            break
        default:
            mWeight = sheetWeight({
                density,
                thickness,
                width,
                height,
                useImperialUnits,
            })
            break
    }
    return mWeight
}

export const calculateRatePrice = (
    val,
    profile,
    { density, diameter, height, materialLength, thickness, useImperialUnits, webThickness, width }
) => {
    let result = 0.0

    if (profile === 'Square') {
        height = width
    }

    const mWeight = materialWeight(
        profile,
        density,
        thickness,
        materialLength,
        width,
        height,
        useImperialUnits,
        diameter,
        webThickness
    )

    if (mWeight > 0) {
        result = val / mWeight
        result = parseFloat(result).toFixed(2)
    }
    return result
}

export const calculateCostPrice = (
    val,
    profile,
    { density, diameter, height, materialLength, thickness, useImperialUnits, webThickness, width }
) => {
    let result = 0.0

    if (profile === 'Square') {
        height = width
    }

    const mWeight = materialWeight(
        profile,
        density,
        thickness,
        materialLength,
        width,
        height,
        useImperialUnits,
        diameter,
        webThickness
    )

    if (mWeight > 0) {
        result = val * mWeight
        result = parseFloat(result).toFixed(2)
    }
    return result
}

export const getPartDimensions = (part) => {
    if (!part) return null
    return {
        profile: part.profile,
        displayBoundBoxHeight: part.displayBoundBoxHeight,
        displayBoundBoxWidth: part.displayBoundBoxWidth,
        rotaryProfileLength: part.rotaryProfileLength,
        rotaryProfileWidth: part.rotaryProfileWidth,
        diameter: part.diameter,
        isImperial: part.isImperial,
    }
}

export const getPartDimensionsDisplay = (part, dimensionFormat) => {
    if (!part || !part.profile || typeof dimensionFormat !== 'function') return null
    switch (part.profile.toLowerCase()) {
        case 'square':
            return part.rotaryProfileWidth
        case 'circle':
            return part.diameter
        default:
            return `${dimensionFormat(part.rotaryProfileWidth)} x ${dimensionFormat(part.rotaryProfileLength)}`
    }
}

export const getRecordDimensionsDisplay = (sheet, dimensionFormat) => {
    if (!sheet || !sheet.profile || typeof dimensionFormat !== 'function') return null
    switch (sheet.profile.toLowerCase()) {
        case 'square':
            return sheet.sheetWidth
        case 'circle':
            return sheet.diameter
        default:
            return `${dimensionFormat(sheet.sheetWidth)} x ${dimensionFormat(sheet.sheetHeight)}`
    }
}

/**
 * Formats a record's thickness for display. An IBeam has two: the flange thickness in
 * `thickness` and the web thickness, which is the only value distinguishing two otherwise
 * identical beam records. Renders them as "web/flange" (web first, the tw/tf steel-section
 * convention, matching the quote export from QM-3685). Every other profile renders its single
 * thickness unchanged, so call sites can adopt this unconditionally.
 *
 * Deliberately separate from getRecordDimensionsDisplay, which encodes dimensions only.
 * @param {Object} record - Order item, sheet record, dropdown option or part library entry
 *   (profile, thickness, webThickness)
 * @param {Function} dimensionFormat - Number formatter (e.g. the `n` from useNumberFormatter)
 * @returns {string} e.g. '8/12' for an IBeam, '12' otherwise, or '' when there is no thickness
 *   to show (null/undefined record, or a missing/blank thickness)
 */
export const getThicknessDisplay = (record, dimensionFormat) => {
    if (!record || !record.thickness) return ''

    const { profile, thickness, webThickness } = record

    if (profile !== MATERIAL_PROFILE_TYPES.IBeam.value || !webThickness) {
        return dimensionFormat(thickness)
    }

    return `${dimensionFormat(webThickness)}/${dimensionFormat(thickness)}`
}

/**
 * Whether a record carries a second, web thickness: only an IBeam does, and only once the value
 * is known. Callers use it to caption the "web/flange" pair that getThicknessDisplay renders.
 * @param {Object} record - Sheet/record object (profile, webThickness)
 * @returns {boolean}
 */
export const hasWebThickness = (record) =>
    record?.profile === MATERIAL_PROFILE_TYPES.IBeam.value && Boolean(record.webThickness)

/**
 * Names the order of the values in a size string, for the small caption shown above it. The
 * value itself stays the plain "100 x 50 x 3000" string; this only says which number is which.
 * Returns the English text, which is also the translation key.
 * @param {Object} shape
 * @param {boolean} [shape.isCircle] - A circle reads diameter then length
 * @param {boolean} [shape.hasLength] - A profile adds its length after width and height
 * @returns {string} 'Ø x L', 'W x H x L' or 'W x H'
 */
export const getSizeCaptionKey = ({ hasLength, isCircle } = {}) => {
    if (isCircle) return 'Ø x L'
    return hasLength ? 'W x H x L' : 'W x H'
}

/**
 * Builds the composite key that keeps two IBeam records with the same flange thickness but
 * different web thickness as distinct thickness-dropdown options. Non-IBeam profiles key on
 * thickness alone, unchanged from the previous single-value behaviour.
 * @param {Object} record - Sheet/record object (profile, thickness, webThickness)
 * @returns {string} A primitive suitable for a MUI Select value; '' when there is no thickness
 */
export const getThicknessOptionValue = ({ profile, thickness, webThickness } = {}) => {
    if (!thickness) return ''
    return profile === MATERIAL_PROFILE_TYPES.IBeam.value ? `${webThickness ?? ''}|${thickness}` : String(thickness)
}

export const isProfileSizeFit = (sheet, partDimensions, organisation) => {
    if (
        !sheet ||
        !partDimensions ||
        (sheet.profile && partDimensions.profile && sheet.profile !== partDimensions.profile)
    ) {
        return false
    }

    if (!sheet.profile) {
        //we check later if a flat part can fit on a sheet, currently we do not have the means
        //of letting a user know that their part wont fit on a sheet on the part details panel
        //this if statement can be removed when we handle this case better later
        return true
    }

    const orgUsesImperial = organisation.defaultDrawingUnits.toLowerCase() === 'imperial'
    const orgAndPartUnitsAreEqual = partDimensions.isImperial === orgUsesImperial

    const convertedPartDimensions = {
        rotaryProfileWidth:
            !partDimensions.rotaryProfileWidth || orgAndPartUnitsAreEqual
                ? partDimensions.rotaryProfileWidth
                : orgUsesImperial
                  ? mmToInches(partDimensions.rotaryProfileWidth)
                  : inchesToMm(partDimensions.rotaryProfileWidth),
        rotaryProfileLength:
            !partDimensions.rotaryProfileLength || orgAndPartUnitsAreEqual
                ? partDimensions.rotaryProfileLength
                : orgUsesImperial
                  ? mmToInches(partDimensions.rotaryProfileLength)
                  : inchesToMm(partDimensions.rotaryProfileLength),
        diameter:
            !partDimensions.diameter || orgAndPartUnitsAreEqual
                ? partDimensions.diameter
                : orgUsesImperial
                  ? mmToInches(partDimensions.diameter)
                  : inchesToMm(partDimensions.diameter),
        displayBoundBoxHeight:
            !partDimensions.displayBoundBoxHeight || orgAndPartUnitsAreEqual
                ? partDimensions.displayBoundBoxHeight
                : orgUsesImperial
                  ? mmToInches(partDimensions.displayBoundBoxHeight)
                  : inchesToMm(partDimensions.displayBoundBoxHeight),
        displayBoundBoxWidth:
            !partDimensions.displayBoundBoxWidth || orgAndPartUnitsAreEqual
                ? partDimensions.displayBoundBoxWidth
                : orgUsesImperial
                  ? mmToInches(partDimensions.displayBoundBoxWidth)
                  : inchesToMm(partDimensions.displayBoundBoxWidth),
    }

    const width = partDimensions.profile
        ? convertedPartDimensions.rotaryProfileWidth
        : convertedPartDimensions.displayBoundBoxWidth
    const height = partDimensions.profile
        ? convertedPartDimensions.rotaryProfileLength
        : convertedPartDimensions.displayBoundBoxHeight

    switch (sheet.profile?.toLowerCase()) {
        case 'square':
            return dimensionsSameWithTolerance(
                sheet.sheetWidth,
                convertedPartDimensions.rotaryProfileWidth,
                organisation,
                true
            )
        case 'circle':
            return dimensionsSameWithTolerance(sheet.diameter, convertedPartDimensions.diameter, organisation, true)
        case 'flatsidedoval':
        case 'angle':
        case 'uchannel':
        case 'ibeam':
        case 'tbeam':
            // Open-section profiles have a fixed orientation (unlike rectangle), so width and
            // height must each match without the either-orientation swap below.
            return (
                dimensionsSameWithTolerance(sheet.sheetWidth, width, organisation, true) &&
                dimensionsSameWithTolerance(sheet.sheetHeight, height, organisation, true)
            )
        case 'rectangle':
            return (
                (dimensionsSameWithTolerance(sheet.sheetWidth, width, organisation, true) &&
                    dimensionsSameWithTolerance(sheet.sheetHeight, height, organisation, true)) ||
                (dimensionsSameWithTolerance(sheet.sheetHeight, width, organisation, true) &&
                    dimensionsSameWithTolerance(sheet.sheetWidth, height, organisation, true))
            )
        default:
            return (
                partFitsWithTolerance(sheet.sheetHeight, width, organisation) &&
                partFitsWithTolerance(sheet.sheetWidth, height, organisation)
            )
    }
}

export const thicknessSameWithTolerance = (
    sheet,
    partThickness,
    partDimensions,
    organisation,
    usePercentage = false
) => {
    const orgUsesImperial = organisation.defaultDrawingUnits.toLowerCase() === 'imperial'
    const orgAndPartUnitsAreEqual = partDimensions.isImperial === orgUsesImperial
    const convertedPartThickness = orgAndPartUnitsAreEqual
        ? partThickness
        : orgUsesImperial
          ? mmToInches(partThickness)
          : inchesToMm(partThickness)

    return dimensionsSameWithTolerance(sheet.thickness, convertedPartThickness, organisation, usePercentage)
}

const PROFILE_SIZE_TOLERANCE_PERCENTAGE = 2

const dimensionsSameWithTolerance = (sheetDimension, partDimension, organisation, usePercentage = false) => {
    const precision = organisationLengthPrecision(organisation)
    if (usePercentage) {
        return numbersWithinPercentageTolerance(
            partDimension?.toFixed(precision),
            sheetDimension?.toFixed(precision),
            PROFILE_SIZE_TOLERANCE_PERCENTAGE
        )
    } else {
        return (
            Math.abs(partDimension?.toFixed(precision) - sheetDimension?.toFixed(precision)) <=
            organisationDimensionTolerance(organisation)
        )
    }
}

const partFitsWithTolerance = (sheetDimension, partDimension, organisation) => {
    const precision = organisationLengthPrecision(organisation)
    return (
        partDimension?.toFixed(precision) - sheetDimension?.toFixed(precision) <=
        organisationDimensionTolerance(organisation)
    )
}
